import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { bulkCreateScheduleItems, fetchPartners } from "api";
import { PageHeader, FormField, Spinner, Select } from "components/common";
import type { PartnerEntity } from "types";

interface SlotEntry {
  id:   number;
  date: string;
  time: string;
}

function formatTime12h(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const ampm  = h < 12 ? "am" : "pm";
  const hour  = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")}${ampm}`;
}

let nextId = 1;

function newSlot(): SlotEntry {
  return { id: nextId++, date: "", time: "" };
}

export default function ImportScheduleWizard() {
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();

  const [step, setStep]                     = useState<1 | 2 | 3>(1);
  const [partnerEntityId, setPartnerEntityId] = useState("");
  const [slots, setSlots]                   = useState<SlotEntry[]>([newSlot()]);

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const donators = partners.filter((p: PartnerEntity) => p.type === "donator" && p.active);
  const selectedPartner = donators.find((p) => String(p.id) === partnerEntityId);

  const { mutate, isPending, error } = useMutation({
    mutationFn: () => {
      const items = validSlots.map((slot) => ({
        partner_entity_id: Number(partnerEntityId),
        scheduled_at:      new Date(`${slot.date}T${slot.time}`).toISOString(),
        status:            "created",
      }));
      return bulkCreateScheduleItems(items);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scheduleItems"] });
      navigate("/add-new");
    },
  });

  const validSlots = slots.filter((s) => s.date && s.time);
  const canProceedStep1 = !!partnerEntityId;
  const canProceedStep2 = validSlots.length > 0;

  const addSlot = () => setSlots((prev) => [...prev, newSlot()]);

  const removeSlot = (id: number) =>
    setSlots((prev) => (prev.length > 1 ? prev.filter((s) => s.id !== id) : prev));

  const updateSlot = (id: number, field: "date" | "time", value: string) =>
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  return (
    <div className="px-8 py-8 max-w-lg">
      <button
        onClick={() => (step > 1 ? setStep((s) => (s - 1) as 1 | 2 | 3) : navigate(-1))}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 transition-colors"
      >
        ← Back
      </button>

      <PageHeader
        title="Import schedule"
        subtitle={`Step ${step} of 3`}
      />

      <div className="card p-6 space-y-5">

        {/* ── Step 1: Select donator ─────────────────────────────────────── */}
        {step === 1 && (
          <>
            <FormField label="Donator partner">
              <Select value={partnerEntityId} onChange={(e) => setPartnerEntityId(e.target.value)}>
                <option value="" disabled>Select a donator…</option>
                {donators.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </FormField>

            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!canProceedStep1}
                onClick={() => setStep(2)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* ── Step 2: Add time slots ─────────────────────────────────────── */}
        {step === 2 && (
          <>
            <div className="space-y-3">
              <p className="text-sm text-gray-500">
                Add pickup slots for <span className="font-medium text-gray-800">{selectedPartner?.name}</span>.
              </p>

              {slots.map((slot, i) => (
                <div key={slot.id} className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 w-5 shrink-0">{i + 1}.</span>
                  <input
                    type="date"
                    className="input py-1 text-sm"
                    value={slot.date}
                    onChange={(e) => updateSlot(slot.id, "date", e.target.value)}
                  />
                  <input
                    type="time"
                    className="input py-1 text-sm"
                    value={slot.time}
                    onChange={(e) => updateSlot(slot.id, "time", e.target.value)}
                  />
                  <button
                    type="button"
                    className="text-gray-300 hover:text-red-400 text-lg leading-none shrink-0"
                    onClick={() => removeSlot(slot.id)}
                    disabled={slots.length === 1}
                    title="Remove slot"
                  >
                    ×
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="btn-secondary py-1 px-3 text-xs"
                onClick={addSlot}
              >
                + Add slot
              </button>
            </div>

            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
                Back
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={!canProceedStep2}
                onClick={() => setStep(3)}
              >
                Next
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: Confirm ───────────────────────────────────────────── */}
        {step === 3 && (
          <>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                Confirm creation of{" "}
                <span className="font-semibold">{validSlots.length}</span>{" "}
                appointment{validSlots.length !== 1 ? "s" : ""}?
              </p>

              <div className="text-xs text-gray-500 space-y-0.5">
                <p className="font-medium text-gray-700 mb-1">{selectedPartner?.name}</p>
                {validSlots.map((slot, i) => (
                  <p key={slot.id}>
                    {i + 1}. {slot.date} at {formatTime12h(slot.time)}
                  </p>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setStep(2)}>
                Back
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={isPending}
                onClick={() => mutate()}
              >
                {isPending ? <Spinner className="h-4" /> : "Create appointments"}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
